import { Icon, Tooltip } from '@components/ui';
import { AssessmentOptions, DataCollectionOptions } from '@types';
import clsx from 'clsx';

import { ApiApi, QuestionnaireApi } from '../api-client';

import { depaginate } from './depaginate';
import styles from './Status.module.scss';

function fillIdWithName<T extends { name: string }>(entity: T): T & { id: string } {
  return { ...entity, id: entity.name };
}

const getAssessmentIconStatus = (status: string | undefined) => {
  switch (status) {
    case 'RUNNING':
      return (
        <Tooltip
          className={styles.tooltip}
          placement="right"
          content="Assessment is currently running"
          hideArrow
          isFixed
        >
          <div className={clsx(styles.icon, styles.running)}>
            <Icon name="arrows-rotate" />
          </div>
        </Tooltip>
      );
    case 'FAILED':
      return (
        <Tooltip
          className={styles.tooltip}
          placement="right"
          content="Assessment failed"
          hideArrow
          isFixed
        >
          <div className={clsx(styles.icon, styles.failed)}>
            <Icon name="exclamation" />
          </div>
        </Tooltip>
      );
    case 'SUCCESS':
      return (
        <Tooltip
          className={styles.tooltip}
          placement="right"
          content="Assessment successful"
          hideArrow
          isFixed
        >
          <div className={clsx(styles.icon, styles.success)}>
            <Icon name="check" />
          </div>
        </Tooltip>
      );
    case 'SUCCESS_WITH_ERRORS':
      return (
        <Tooltip
          className={styles.tooltip}
          placement="right"
          content="Assessment successful with errors"
          hideArrow
          isFixed
        >
          <div className={clsx(styles.icon, styles.success)}>
            <Icon name="exclamation" />
          </div>
        </Tooltip>
      );
    case 'PENDING':
      return (
        <Tooltip
          className={styles.tooltip}
          placement="right"
          content="The assessment is pending"
          hideArrow
          isFixed
        >
          <div className={clsx(styles.icon, styles.pending)}>
            <Icon name="clock" />
          </div>
        </Tooltip>
      );

    default:
      return (
        <Tooltip
          className={styles.tooltip}
          placement="right"
          content="Questionnaire not submitted"
          hideArrow
          isFixed
        >
          <div className={clsx(styles.icon, styles.notStarted)}>
            <Icon name="dash" />
          </div>
        </Tooltip>
      );
  }
};

interface GetMetricIconStatusProps {
  status: string | undefined;
  placement?: 'right' | 'left' | 'bottom' | 'top' | 'bottom-start' | 'bottom-end' | 'top-start';
  message?: string;
}

export const getMetricIconStatus = (props: GetMetricIconStatusProps) => {
  switch (props.status) {
    case 'RUNNING':
      return (
        <Tooltip
          className={styles.metricTooltip}
          content="Metric is currently running"
          hideArrow
          placement="right"
        >
          <div className={clsx(styles.icon, styles.running)}>
            <Icon name="arrows-rotate" />
          </div>
        </Tooltip>
      );
    case 'FAILED':
      return (
        <Tooltip
          className={styles.metricTooltip}
          placement={props.placement || 'right'}
          content={props.message}
          hideArrow
        >
          <div className={clsx(styles.icon, styles.failed)}>
            <Icon name="exclamation" />
          </div>
        </Tooltip>
      );
    case 'SUCCESS':
      return (
        <Tooltip
          className={styles.metricTooltip}
          placement={props.placement || 'right'}
          content="Metric successful"
          hideArrow
        >
          <div className={clsx(styles.icon, styles.success)}>
            <Icon name="check" />
          </div>
        </Tooltip>
      );
    case 'SUCCESS_WITH_ERRORS':
      return (
        <Tooltip
          className={styles.metricTooltip}
          placement={props.placement || 'right'}
          content={props.message || 'Metric successful with errors'}
          hideArrow
        >
          <div className={clsx(styles.icon, styles.success)}>
            <Icon name="exclamation" />
          </div>
        </Tooltip>
      );
    case 'SUCCESS_WITH_WARNINGS':
      return (
        <Tooltip
          className={styles.metricTooltip}
          placement={props.placement || 'right'}
          content={props.message || 'Metric successful with warnings'}
          hideArrow
        >
          <div className={clsx(styles.icon, styles.success)}>
            <Icon name="exclamation" />
          </div>
        </Tooltip>
      );
    case 'PENDING':
      return (
        <Tooltip
          className={styles.metricTooltip}
          placement={props.placement || 'right'}
          content="The metric is pending"
          hideArrow
        >
          <div className={clsx(styles.icon, styles.pending)}>
            <Icon name="clock" />
          </div>
        </Tooltip>
      );

    default:
      return (
        <Tooltip
          className={styles.metricTooltip}
          placement={props.placement || 'top'}
          content="Metric not submitted"
          hideArrow
        >
          <div className={clsx(styles.icon, styles.notStarted)}>
            <Icon name="dash" />
          </div>
        </Tooltip>
      );
  }
};

export async function fetchInitialOptions(
  api: ApiApi,
  questionnaireApi: QuestionnaireApi,
): Promise<DataCollectionOptions | null> {
  try {
    return await Promise.all([
      depaginate(page => api.apiAiModelsList({ page, pageSize: 100 })),
      depaginate(page => questionnaireApi.apiProtectedGroupsList({ page })),
      depaginate(page => api.apiPerformanceMetricsList({ page })),
    ]).then(([models, protectedGroups, performanceMetrics]) => ({
      models: models.map(model => ({
        ...model,
        assessmentStatus: model.latestAssessmentStatus,
        icon: getAssessmentIconStatus(model.latestAssessmentStatus),
      })),
      protectedGroups,
      performanceMetrics,
    }));
  } catch (error) {
    console.log(error);
    return null;
  }
}

export async function fetchInitialAssessmentOptions(api: ApiApi): Promise<AssessmentOptions> {
  const useCases = await api.apiUseCaseNamesList();
  return { useCases };
}
